import React, { FC, ReactNode } from 'react';

import { type Action } from '~/lib/authz/eval';
import { BankAccountSelector, CounterpartySelector } from '~/lib/components/selectors';

import { type SequenceRow, TransferSequence } from './TransferSequence';

interface AccountField {
  value: string;
  onChange: (id: string) => void;
  onBlur: () => void;
  hasError?: boolean;
  label?: string;
  description?: string;
  dataCy?: string;
}

export interface AccountsSequenceProps {
  bankAccount: AccountField & {
    permission?: Action['permission'];
    unauthorizedText?: string;
    amount?: number;
    amountCurrency?: string;
  };
  counterparty: AccountField & {
    counterpartyType: string;
    onCreate?: () => void;
    alert?: ReactNode;
  };
  /** The origin renders on top; a bank-account origin also gets its balance checked. */
  originatesFrom: 'bankAccount' | 'counterparty';
}

export const AccountsSequence: FC<AccountsSequenceProps> = ({ bankAccount, counterparty, originatesFrom }) => {
  const bankIsSource = originatesFrom === 'bankAccount';

  const bankRow: SequenceRow = {
    key: 'bankAccount',
    label: bankAccount.label ?? 'Bank Account',
    description: bankAccount.description,
    required: true,
    selected: Boolean(bankAccount.value),
    node: (
      <BankAccountSelector
        value={bankAccount.value}
        onChange={bankAccount.onChange}
        onBlur={bankAccount.onBlur}
        hasError={bankAccount.hasError}
        permission={bankAccount.permission}
        unauthorizedText={bankAccount.unauthorizedText}
        amount={bankAccount.amount}
        amountCurrency={bankAccount.amountCurrency}
        isSource={bankIsSource}
        dataCy={bankAccount.dataCy}
      />
    ),
  };

  const counterpartyRow: SequenceRow = {
    key: 'counterparty',
    label: counterparty.label ?? 'Counterparty',
    description: counterparty.description,
    required: true,
    selected: Boolean(counterparty.value),
    node: (
      <CounterpartySelector
        value={counterparty.value}
        onChange={counterparty.onChange}
        onBlur={counterparty.onBlur}
        hasError={counterparty.hasError}
        counterpartyType={counterparty.counterpartyType}
        onCreate={counterparty.onCreate}
        alert={counterparty.alert}
        dataCy={counterparty.dataCy}
      />
    ),
  };

  const rows = bankIsSource ? [bankRow, counterpartyRow] : [counterpartyRow, bankRow];

  return <TransferSequence rows={rows} />;
};
